import moment from "moment";
import * as qs from "qs";
import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Container,
  Dropdown,
  Row,
} from "react-bootstrap";
import {
  ArrowDownCircleFill,
  ArrowLeftCircleFill,
  ArrowRightCircleFill,
  ArrowUpCircleFill,
  Calendar2Check,
  ChevronLeft,
  ChevronRight,
  ClockFill,
  SendFill,
} from "react-bootstrap-icons";
import { Link, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import { makeRESTApiRequests } from "../../helpers/api";
import {
  DRAFTSMAN_ROLE,
  ENDPOINTS,
  FIELD_STAFF_ROLE,
  REVIEWER_ROLE,
  scheduleTimeDomains,
} from "../../helpers/constants";
import { dateMinusDate, dateMinusToday } from "../../helpers/global";
import useAppChoices from "../../hooks/useAppChoices";
import AlertModal from "../AlertModal";
import FloatingButton from "../FloatingButton";
import Loader from "../Loader";
import useTimeTracker from "../../hooks/useTimeTracker";
import RippleEffect from "../RippleEffect";
import { useScreenWidth } from "../../hooks/useScreenWidth";

const typesOfProjection = [
  { key: "titleClosingDate", label: "Title Closing Date" },
  { key: "agreedDueDate", label: "Agreed Due Date" },
];

const timeDomainNextStatusMap = {
  "Field Time": "Drafting",
  "Drafting Time": "Reviewing",
  "Reviewing Time": "Completed",
};

const timeDomainToUserPropertyMap = {
  "Field Time": "fieldStaff",
  "Drafting Time": "draftsman",
  "Reviewing Time": "reviewer",
};

const timeDomainToRoleMap = {
  "Field Time": FIELD_STAFF_ROLE,
  "Drafting Time": DRAFTSMAN_ROLE,
  "Reviewing Time": REVIEWER_ROLE,
};

const getDefaultTimeDomain = (timeDomainFromURL = "Field Time") => {
  if (!scheduleTimeDomains.includes(timeDomainFromURL))
    return scheduleTimeDomains[0];
  return timeDomainFromURL;
};

const JobSlot = ({
  job,
  paddingClass,
  size,
  index,
  date,
  projectionType,
  timeDomain,
  onJobClick,
  isSelected,
  selectionArrowType,
  onJobToggleStatusClick,
  areJobsToBeRescheduled,
  onJobToggleCrewClick,
  crewIndex,
  openModal,
  activeTimeEntry,
  setActiveJobDetails,
}) => {
  return (
    <Col
      className={` ${paddingClass}`}
      style={{ fontSize: 12 }}
      xs={job["blocksOccupiedToday"] * 3}
    >
      {job ? (
        <Job
          job={job}
          projectionType={projectionType}
          timeDomain={timeDomain}
          scheduleDate={date}
          onJobClick={onJobClick}
          isSelected={isSelected}
          selectionArrowType={selectionArrowType}
          onJobToggleStatusClick={onJobToggleStatusClick}
          onJobToggleCrewClick={onJobToggleCrewClick}
          crewIndex={crewIndex}
          areJobsToBeRescheduled={areJobsToBeRescheduled}
          openModal={openModal}
          activeTimeEntry={activeTimeEntry}
          setActiveJobDetails={setActiveJobDetails}
        />
      ) : (
        <Alert variant="primary" className="mb-0 text-left p-2 h-100">
          Rest Hour
        </Alert>
      )}
    </Col>
  );
};

const Job = ({
  job = {},
  scheduleDate,
  projectionType,
  timeDomain,
  maxWidth,
  onJobClick,
  isSelected,
  selectionArrowType = "up",
  onJobToggleStatusClick,
  onJobToggleCrewClick,
  areJobsToBeRescheduled,
  crewIndex,
  openModal,
  activeTimeEntry,
  setActiveJobDetails,
}) => {
  const { screenWidth } = useScreenWidth();
  const mobileView = useMemo(() => screenWidth <= 800, [screenWidth]);

  const handleOpenModalWithJob = (e) => {
    e.stopPropagation();
    setActiveJobDetails(job);
    openModal();
  };

  let dueDiff = dateMinusDate(job["agreedDueDate"], scheduleDate);
  let closingDiff = dateMinusDate(job["titleClosingDate"], scheduleDate);

  const variant = !isSelected
    ? (projectionType === typesOfProjection[1]?.label && dueDiff < 3) ||
      (projectionType === typesOfProjection[0]?.label && closingDiff < 3)
      ? "danger"
      : "primary"
    : "secondary";

  const ButtonContent =
    job._id === activeTimeEntry?.job?._id ? (
      <Button
        size="sm"
        variant="primary"
        className="py-1 px-1 mr-0 tiny-text"
        onClick={handleOpenModalWithJob}
      >
        <RippleEffect size={10} className="mr-0" />
      </Button>
    ) : (
      <Button
        size="sm"
        variant="primary"
        className="py-1 px-1 mr-0 tiny-text d-flex align-items-center"
        onClick={handleOpenModalWithJob}
      >
        <ClockFill size={10} className="mr-0" />
      </Button>
    );

  return (
    <Alert
      onClick={onJobClick}
      variant={variant}
      style={{
        border: job["invoicePaid"] ? "3px solid #28a745" : "none",
      }}
      className={`mb-0 text-left p-1 h-100 tiny-text ${
        onJobClick ? "hover-light" : ""
      }`}
      // style={{ minWidth: "150px" }}
    >
      {isSelected &&
        (selectionArrowType === "up" ? (
          <ArrowUpCircleFill
            style={{ position: "absolute", right: -5, top: -5 }}
            className="text-success"
            size={18}
          />
        ) : (
          <ArrowDownCircleFill
            style={{ position: "absolute", right: -5, top: -5 }}
            className="text-success"
            size={18}
          />
        ))}
      <div className="d-flex">
        <div className="flex-grow-1">
          {!areJobsToBeRescheduled && (
            <SendFill
              onClick={(e) => {
                e.stopPropagation();
                onJobToggleCrewClick(job, timeDomain, crewIndex);
              }}
              size={13}
              className="text-primary hover-light mr-2"
            />
          )}
          {!mobileView && (
            <Link
              onClick={(e) => e.stopPropagation()}
              target="_blank"
              to={`/job/${job["_id"]}`}
              className="alert-link"
              style={{ ...textWrapStyle }}
            >
              {job["propertyAddress"]}, {job["propertyStreet"]}, {job["city"]},{" "}
              {job["county"]}
            </Link>
          )}
        </div>
        {!areJobsToBeRescheduled && <div>{ButtonContent}</div>}
      </div>

      {mobileView && (
        <Link
          onClick={(e) => e.stopPropagation()}
          target="_blank"
          to={`/job/${job["_id"]}`}
          className="alert-link mt-1 xtiny-text"
          style={{ ...textWrapStyle }}
        >
          {job["propertyAddress"]}, {job["propertyStreet"]}, {job["city"]},{" "}
          {job["county"]}
        </Link>
      )}

      <div className="mt-1 d-flex flex-wrap align-items-center gap-1">
        {" "}
        {job["invoicePaid"] && (
          <Badge style={{ ...textWrapStyle }} variant="success">
            Paid
          </Badge>
        )}
        {job["isThereAClosing"] && (
          <Badge style={{ ...textWrapStyle }} variant="info">
            Closing
          </Badge>
        )}
        {timeDomain === "Field Time" && (
          <Badge style={{ ...textWrapStyle }} variant="dark" className="">
            {job?.["status"]?.replace("Field", "F")}
          </Badge>
        )}
        <Badge
          style={{
            wordWrap: "break-word",
            wordBreak: "break-word",
            whiteSpace: "normal",
          }}
          variant="dark"
          className=""
        >
          {job?.["typeOfLandSurvey"]}
        </Badge>
        {job?.["comment"] && (
          <Badge style={{ ...textWrapStyle }} className="" variant="light">
            {job?.["comment"]?.slice(0, 20)}
          </Badge>
        )}
      </div>
      <hr className="my-1" />

      <div className="d-flex">
        <div className="flex-grow-1">
          <h6 className="mb-0 smallFont">
            <b>{mobileView ? "D" : "Due"}: </b>
            <span style={{ ...textWrapStyle }}>
              {" "}
              {job["agreedDueDate"]
                ? `${moment(job["agreedDueDate"])?.format(
                    "MM/DD"
                  )} (${dueDiff}) `
                : "N/A "}
            </span>
          </h6>

          {job["titleClosingDate"] ? (
            <>
              <h6 className="mb-0 smallFont">
                <b>{mobileView ? "C" : "Closing"}: </b>
                <span style={{ ...textWrapStyle }}>
                  {job["titleClosingDate"]
                    ? `${moment(job["titleClosingDate"]).format(
                        "MM/DD"
                      )} (${closingDiff}) `
                    : "N/A "}
                </span>
              </h6>
            </>
          ) : (
            ""
          )}

          {job["acreage"] ? (
            <>
              <b>{mobileView ? "A" : "Acr"}: </b>
              <span style={{ ...textWrapStyle }}>{job["acreage"]}</span>
            </>
          ) : (
            ""
          )}
        </div>
        {!mobileView && !areJobsToBeRescheduled && (
          <div>
            {timeDomainNextStatusMap[timeDomain] && (
              <ArrowRightCircleFill
                onClick={(e) => {
                  e.stopPropagation();
                  onJobToggleStatusClick(job, timeDomain);
                }}
                size={16}
                className="text-success hover-light"
              />
            )}
          </div>
        )}
      </div>
      {mobileView && !areJobsToBeRescheduled && (
        <div className="text-right mt-1">
          {timeDomainNextStatusMap[timeDomain] && (
            <ArrowRightCircleFill
              onClick={(e) => {
                e.stopPropagation();
                onJobToggleStatusClick(job, timeDomain);
              }}
              size={16}
              className="text-success hover-light"
            />
          )}
        </div>
      )}
    </Alert>
  );
};

const CrewSchedule = () => {
  const location = useLocation();
  const params = qs.parse(location.search, { ignoreQueryPrefix: true });

  const [loading, setLoading] = useState(false);
  const [projectedSchedule, setProjectedSchedule] = useState();
  const [projectionTypes] = useState([...typesOfProjection]);
  const [projectionType, setProjectionType] = useState(
    projectionTypes[0]?.label
  );
  const [timeDomain, setTimeDomain] = useState(
    getDefaultTimeDomain(params.timeDomain)
  );
  const [jobsToBeMarkedAsUrgent, setJobsToBeMarkedAsUrgent] = useState([]);
  const [jobsToBeRemovedFromUrgent, setJobsToBeRemovedFromUrgent] = useState(
    []
  );
  const [statusChangeModalMeta, setStatusChangeModalMeta] = useState(null);
  const [crewChangeModalMeta, setCrewChangeModalMeta] = useState(null);
  const usersAppChoices = useAppChoices("users");
  const {
    activeTimeEntry,
    elapsedTime,
    openModal,
    setActiveJobDetails,
    activeJob,
  } = useTimeTracker();

  const { screenWidth } = useScreenWidth();

  const todaysDate = useMemo(() => new Date(), []);
  const mobileView = useMemo(() => screenWidth <= 800, [screenWidth]);
  const [activeIndex, setActiveIndex] = useState(0);

  const areJobsToBeRescheduled = useMemo(
    () =>
      jobsToBeMarkedAsUrgent.length > 0 || jobsToBeRemovedFromUrgent.length > 0,
    [jobsToBeMarkedAsUrgent, jobsToBeRemovedFromUrgent]
  );

  const fetchJobs = async () => {
    setLoading(true);

    const { response, error } = await makeRESTApiRequests({
      endpoint: ENDPOINTS.GET_JOB_SCHEDULE,
      requestBody: {
        projectionType: projectionTypes.find(
          (pt) => pt?.label === projectionType
        )?.key,
        timeParam: timeDomain,
        addUrgent: jobsToBeMarkedAsUrgent,
        removeUrgent: jobsToBeRemovedFromUrgent,
      },
    });

    if (error) {
      toast.error(error);
      setLoading(false);
      return;
    }

    setProjectedSchedule(response?.schedule);

    setLoading(false);
  };

  const onScheduleJobClick = (job) => {
    const jobId = job["_id"];
    if (jobsToBeMarkedAsUrgent.includes(jobId)) {
      jobsToBeMarkedAsUrgent.splice(jobsToBeMarkedAsUrgent.indexOf(jobId), 1);
    } else {
      jobsToBeMarkedAsUrgent.push(jobId);
    }
    setJobsToBeMarkedAsUrgent([...jobsToBeMarkedAsUrgent]);
  };

  const onUrgentJobClick = (job) => {
    const jobId = job["_id"];
    if (jobsToBeRemovedFromUrgent.includes(jobId)) {
      jobsToBeRemovedFromUrgent.splice(
        jobsToBeRemovedFromUrgent.indexOf(jobId),
        1
      );
    } else {
      jobsToBeRemovedFromUrgent.push(jobId);
    }
    setJobsToBeRemovedFromUrgent([...jobsToBeRemovedFromUrgent]);
  };

  useEffect(() => {
    fetchJobs();
  }, [projectionType, timeDomain]);

  useEffect(() => {
    setJobsToBeMarkedAsUrgent([]);
    setJobsToBeRemovedFromUrgent([]);
  }, [projectedSchedule]);

  const TimeLine = () => {
    if (!projectedSchedule) return "";

    return (
      <div className="pb-5">
        <ScheduleInfo />
        <CrewHeaders />
        <UrgentJobs />
        <Schedules />
        {projectedSchedule["outliers"] &&
          projectedSchedule["outliers"].length > 0 && <Outliers />}
      </div>
    );
  };

  const ScheduleInfo = () => {
    return (
      <>
        <Row>
          <Col xs={12} md={4} className="text-left mt-1 ">
            <p className="mb-0 text-secondary" style={{ fontSize: 13 }}>
              <span className="text-dark">
                <b>Schedule Days: </b>
              </span>
              {projectedSchedule["schedules"].length} working days
            </p>
          </Col>
          <Col xs={12} md={4} className="text-left mt-2 mt-md-1 ">
            <p className="mb-0 text-secondary" style={{ fontSize: 13 }}>
              <span className="text-dark">
                <b>Project Schedule Based on: </b>
              </span>
              <Dropdown
                onClick={(e) => e.stopPropagation()}
                className="mb-1 mb-md-0 d-inline-block mr-1"
              >
                <Dropdown.Toggle
                  disabled={loading || areJobsToBeRescheduled}
                  size="sm"
                  variant="transparent "
                  className=" ml-1 text-primary tiny-text"
                >
                  {projectionType}
                </Dropdown.Toggle>

                <Dropdown.Menu>
                  {projectionTypes.map(({ key, label }, index) => (
                    <Dropdown.Item
                      key={index}
                      onClick={() => {
                        setProjectionType(label);
                      }}
                    >
                      {label}
                    </Dropdown.Item>
                  ))}
                </Dropdown.Menu>
              </Dropdown>
            </p>
          </Col>
          <Col xs={12} md={4} className="text-left mt-1 ">
            <p className="mb-0 text-secondary" style={{ fontSize: 13 }}>
              <span className="text-dark">
                <b>Time Domain: </b>
              </span>
              <Dropdown
                onClick={(e) => e.stopPropagation()}
                className="mb-1 mb-md-0 d-inline-block mr-1"
              >
                <Dropdown.Toggle
                  disabled={loading || areJobsToBeRescheduled}
                  size="sm"
                  variant="transparent "
                  className=" ml-1 text-primary tiny-text"
                >
                  {timeDomain}
                </Dropdown.Toggle>

                <Dropdown.Menu>
                  {scheduleTimeDomains.map((s, index) => (
                    <Dropdown.Item
                      key={index}
                      onClick={() => {
                        setTimeDomain(s);
                      }}
                    >
                      {s}
                    </Dropdown.Item>
                  ))}
                </Dropdown.Menu>
              </Dropdown>
            </p>
          </Col>
        </Row>
        <hr />
      </>
    );
  };

  const CrewHeaders = () => {
    return (
      <Row>
        <Col xs={{ offset: 2, span: 10 }} md={{ offset: 1, span: 11 }}>
          {mobileView ? (
            <Alert
              className={`d-flex align-items-center ${
                projectedSchedule?.crews?.length > 1
                  ? "justify-content-between"
                  : "justify-content-center"
              }  m-0`}
              style={{ fontSize: 14, fontWeight: "bold" }}
              variant="success"
            >
              {projectedSchedule?.crews?.length > 1 && (
                <ChevronLeft
                  onClick={() =>
                    setActiveIndex(activeIndex > 0 ? activeIndex - 1 : 0)
                  }
                  className={`hover ${activeIndex <= 0 ? "text-muted" : ""}`}
                  size={20}
                />
              )}{" "}
              {projectedSchedule?.crews?.[activeIndex]}{" "}
              {projectedSchedule?.crews?.length > 1 && (
                <ChevronRight
                  onClick={() =>
                    setActiveIndex(
                      activeIndex < projectedSchedule?.crews?.length - 1
                        ? activeIndex + 1
                        : projectedSchedule?.crews?.length - 1
                    )
                  }
                  className={`hover ${
                    activeIndex >= projectedSchedule?.crews?.length - 1
                      ? "text-muted"
                      : ""
                  }`}
                  size={20}
                />
              )}
            </Alert>
          ) : (
            <Row className="no-gutters">
              {projectedSchedule?.crews &&
                projectedSchedule?.crews?.map((crew, index) => {
                  return (
                    <Col
                      className="pr-1"
                      style={{ fontSize: 14, fontWeight: "bold" }}
                      key={index}
                      xs={12 / projectedSchedule.crews.length}
                    >
                      <Alert variant="success">{crew}</Alert>
                    </Col>
                  );
                })}
            </Row>
          )}
        </Col>
      </Row>
    );
  };

  const Schedules = () => {
    return (
      <>
        <p
          className="mb-2 text-secondary text-left px-3"
          style={{ fontSize: 13 }}
        >
          <span className="text-dark">
            <b>Scheduled Jobs ({projectedSchedule.schedules.length}): </b>
          </span>
        </p>
        {projectedSchedule.schedules.map((s, index) => (
          <ScheduleItem key={s.date} schedule={s} />
        ))}
        <hr />
      </>
    );
  };

  const ScheduleItem = ({ schedule, isUrgent = false }) => {
    return (
      <Row>
        <DateItem date={schedule.date} />
        <Col xs={10} md={11} className="align-self-center p-0">
          {mobileView ? (
            <CrewSchedule
              crewIndex={activeIndex}
              date={schedule.date}
              crewSchedule={schedule?.crewSchedules?.[activeIndex]}
              isUrgent={isUrgent}
            />
          ) : (
            <Row className="pb-1 no-gutters">
              {schedule.crewSchedules.map((crewSchedule, index) => (
                <CrewSchedule
                  key={index}
                  crewIndex={index}
                  date={schedule.date}
                  crewSchedule={crewSchedule}
                  isUrgent={isUrgent}
                />
              ))}
            </Row>
          )}
        </Col>
      </Row>
    );
  };

  const DateItem = ({ date }) => {
    return (
      <Col xs={2} md={1} className="p-0 px-2">
        <VerticalLine />
        <div
          style={{ fontSize: 11 }}
          className="py-2 px-1 my-3 alert bg-dark text-white right-arrow"
        >
          <p className="mb-0">{moment(date).format("Do MMM")}</p>
        </div>
      </Col>
    );
  };

  const VerticalLine = () => {
    return <div style={styles.verticalLine}></div>;
  };

  const CrewSchedule = ({ crewSchedule, crewIndex, date, isUrgent }) => {
    const restHourSize =
      4 -
      (crewSchedule?.jobs?.reduce(
        (acc, job) => acc + job.blocksOccupiedToday,
        0
      ) || 0);

    return (
      <Col
        xs={mobileView ? 12 : 12 / projectedSchedule.crews.length}
        className="pr-1 pb-1"
      >
        {crewSchedule && (
          <Card className="p-1 h-100">
            <Row className="h-100">
              {crewSchedule.jobs.map((j, index, jobs) => (
                <JobSlot
                  key={j["_id"]}
                  job={j}
                  paddingClass={`${index !== jobs.length - 1 ? "pr-1" : ""} ${
                    index !== 0 ? "pl-1" : ""
                  }`}
                  size={jobs.length}
                  index={index}
                  date={date}
                  projectionType={projectionType}
                  timeDomain={timeDomain}
                  onJobClick={() =>
                    isUrgent ? onUrgentJobClick(j) : onScheduleJobClick(j)
                  }
                  isSelected={
                    isUrgent
                      ? jobsToBeRemovedFromUrgent.includes(j["_id"])
                      : jobsToBeMarkedAsUrgent.includes(j["_id"])
                  }
                  onJobToggleStatusClick={onJobToggleStatusClick}
                  onJobToggleCrewClick={onJobToggleCrewClick}
                  crewIndex={crewIndex}
                  areJobsToBeRescheduled={areJobsToBeRescheduled}
                  selectionArrowType={isUrgent ? "down" : "up"}
                  openModal={openModal}
                  activeTimeEntry={activeTimeEntry}
                  setActiveJobDetails={setActiveJobDetails}
                />
              ))}
              {restHourSize > 0 && <RestHour size={restHourSize * 3} />}
            </Row>
          </Card>
        )}
      </Col>
    );
  };

  const RestHour = ({ size }) => {
    return (
      <Col
        className="pl-0"
        style={{ fontSize: 14, fontWeight: "bold" }}
        xs={size}
      >
        <Alert variant="warning" className="mb-0 m-0 text-center p-2 h-100">
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              height: "100%",
            }}
          >
            Rest Hour
          </div>
        </Alert>
      </Col>
    );
  };

  const Outliers = () => {
    return (
      <div className="text-left">
        <h6>Outliers:</h6>
        <Row>
          {projectedSchedule["outliers"].map((o, index) => (
            <OutlierItem job={o} key={index} />
          ))}
        </Row>
      </div>
    );
  };

  const OutlierItem = ({ job }) => {
    return (
      <Col xs={12} md={6}>
        <Card className="my-3 text-left">
          <Card.Header className="p-2">
            {job["propertyAddress"]}, {job["propertyStreet"]}
          </Card.Header>
          <Card.Body className="p-2">
            {job["agreedDueDate"] && (
              <div>
                <p className="mb-0 text-secondary">
                  <span className="text-dark">
                    <b>agreedDueDate: </b>
                  </span>
                  {moment(job["agreedDueDate"]).format("Do MMM, YYYY")} (
                  {dateMinusToday(job["agreedDueDate"])} day/s)
                </p>
              </div>
            )}
            {job["titleClosingDate"] && (
              <div className="mt-1">
                <p className="mb-0 text-secondary">
                  <span className="text-dark">
                    <b>titleClosingDate: </b>
                  </span>
                  {moment(job["titleClosingDate"]).format("Do MMM, YYYY")} (
                  {dateMinusToday(job["titleClosingDate"])} day/s)
                </p>
              </div>
            )}
          </Card.Body>
        </Card>
      </Col>
    );
  };

  const UrgentJobs = () => {
    return (
      <>
        <p
          className="mt-2 mb-2 text-secondary text-left px-3"
          style={{ fontSize: 13 }}
        >
          <span className="text-dark">
            <b>
              Urgent Jobs (
              {projectedSchedule?.urgentSchedules &&
                projectedSchedule?.urgentSchedules?.length}
              ):{" "}
            </b>
          </span>
        </p>
        {projectedSchedule.urgentSchedules.length > 0 ? (
          projectedSchedule.urgentSchedules.map((s, index) => (
            <ScheduleItem key={s.date} schedule={s} isUrgent />
          ))
        ) : (
          <p className="text-muted p-4">No Urgent Jobs To Show!</p>
        )}
        <hr />
      </>
    );
  };

  const onJobToggleStatusClick = (job, timeDomain) => {
    setStatusChangeModalMeta({ job, timeDomain });
  };

  const onJobToggleCrewClick = (job, timeDomain, crewIndex) => {
    if (projectedSchedule.crews.length < 2) {
      toast.error("No user available to assign the job");
      return;
    }
    setCrewChangeModalMeta({ job, timeDomain, crewIndex });
  };

  const onStatusChangeModalHide = () => setStatusChangeModalMeta(null);
  const onCrewChangeModalHide = () => setCrewChangeModalMeta(null);

  const onStatusChangeClick = async () => {
    const { job, timeDomain } = statusChangeModalMeta;

    setStatusChangeModalMeta({ ...statusChangeModalMeta, showProgress: true });

    const { error, response } = await makeRESTApiRequests({
      endpoint: ENDPOINTS.UPDATE_INDIVIDUAL_JOB(job?.["_id"]),
      method: "PUT",
      requestBody: {
        status: timeDomainNextStatusMap[timeDomain],
        projectionType,
        timeParam: timeDomain,
        returnSchedule: true,
      },
    });

    setStatusChangeModalMeta({ ...statusChangeModalMeta, showProgress: false });

    if (error) {
      return toast("Oops, something went wrong!", {
        type: "error",
      });
    }

    toast("Succesfully updated status!", {
      type: "success",
    });

    onStatusChangeModalHide();
    setLoading(true);

    setTimeout(() => {
      setProjectedSchedule(response["schedule"]);
      setLoading(false);
    }, 200);
  };

  const onCrewChangeClick = async () => {
    const { job, timeDomain, crewIndex } = crewChangeModalMeta;

    const role = timeDomainToRoleMap[timeDomain];
    const userProperty = timeDomainToUserPropertyMap[timeDomain];

    const newUserIndex =
      crewIndex + 1 > projectedSchedule.crews.length - 1 ? 0 : crewIndex + 1;
    const newUserName = projectedSchedule.crews[newUserIndex];

    const user = usersAppChoices.find(
      (u) => u.name === newUserName && u.role === role
    );

    if (!user) {
      return toast.error(`No ${role} found in ${newUserName}`);
    }

    setCrewChangeModalMeta({ ...crewChangeModalMeta, showProgress: true });

    const { error, response } = await makeRESTApiRequests({
      endpoint: ENDPOINTS.UPDATE_INDIVIDUAL_JOB(job?.["_id"]),
      method: "PUT",
      requestBody: {
        [userProperty]: user._id,
        projectionType,
        timeParam: timeDomain,
        returnSchedule: true,
      },
    });

    setCrewChangeModalMeta({ ...crewChangeModalMeta, showProgress: false });

    if (error) {
      return toast("Oops, something went wrong!", {
        type: "error",
      });
    }

    toast("Succesfully updated crew!", {
      type: "success",
    });

    onCrewChangeModalHide();
    setLoading(true);

    setTimeout(() => {
      setProjectedSchedule(response["schedule"]);
      setLoading(false);
    }, 200);
  };

  if (loading) {
    return (
      <Container fluid className="py-1 h-100">
        <Loader />
      </Container>
    );
  }

  return (
    <Container fluid className="py-1 px-2 h-100 text-center">
      <TimeLine />
      <AlertModal
        show={statusChangeModalMeta !== null}
        alertText={`Are you sure to change the status of this job to ${
          timeDomainNextStatusMap[statusChangeModalMeta?.timeDomain]
        }?`}
        onHide={onStatusChangeModalHide}
        continueButtonText="Yes"
        onDismissClick={onStatusChangeModalHide}
        dismissButtonText="Cancel"
        progressText={"Updating status..."}
        showProgress={statusChangeModalMeta?.showProgress}
        onContinueClick={onStatusChangeClick}
      />
      <AlertModal
        show={crewChangeModalMeta !== null}
        alertText={`Are you sure to toggle the user of this job?`}
        onHide={onCrewChangeModalHide}
        continueButtonText="Yes"
        onDismissClick={onCrewChangeModalHide}
        dismissButtonText="Cancel"
        progressText={"Updating user..."}
        showProgress={crewChangeModalMeta?.showProgress}
        onContinueClick={onCrewChangeClick}
      />
      {!loading && areJobsToBeRescheduled && (
        <FloatingButton
          text={`Reschedule Jobs (${jobsToBeMarkedAsUrgent.length +
            jobsToBeRemovedFromUrgent.length} jobs)`}
          variant="success"
          Icon={Calendar2Check}
          onClick={fetchJobs}
        />
      )}
    </Container>
  );
};

const styles = {
  verticalLine: {
    background: "#c7c7c7",
    width: 3,
    height: "100%",
    position: "absolute",
    left: "50%",
    top: 0,
  },
};

const textWrapStyle = {
  wordWrap: "break-word",
  wordBreak: "break-word",
  whiteSpace: "normal",
};

export default CrewSchedule;
